/**
 * Note Helper Functions for Glissando
 * Handles clef determination, note positioning, and display logic
 */

// Note order for sorting and comparison
export const NOTE_ORDER = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

// VexFlow note format mapping
export const VEXFLOW_NOTE_MAP = {
  'C2': 'c/2', 'D2': 'd/2', 'E2': 'e/2', 'F2': 'f/2', 'G2': 'g/2', 'A2': 'a/2', 'B2': 'b/2',
  'C3': 'c/3', 'D3': 'd/3', 'E3': 'e/3', 'F3': 'f/3', 'G3': 'g/3', 'A3': 'a/3', 'B3': 'b/3',
  'C4': 'c/4', 'D4': 'd/4', 'E4': 'e/4', 'F4': 'f/4', 'G4': 'g/4', 'A4': 'a/4', 'B4': 'b/4',
  'C5': 'c/5', 'D5': 'd/5', 'E5': 'e/5', 'F5': 'f/5', 'G5': 'g/5', 'A5': 'a/5', 'B5': 'b/5',
  'C6': 'c/6'
};

// MIDI note numbers (for audio)
export const MIDI_NOTE_MAP = {
  'C2': 36, 'D2': 38, 'E2': 40, 'F2': 41, 'G2': 43, 'A2': 45, 'B2': 47,
  'C3': 48, 'D3': 50, 'E3': 52, 'F3': 53, 'G3': 55, 'A3': 57, 'B3': 59,
  'C4': 60, 'D4': 62, 'E4': 64, 'F4': 65, 'G4': 67, 'A4': 69, 'B4': 71,
  'C5': 72, 'D5': 74, 'E5': 76, 'F5': 77, 'G5': 79, 'A5': 81, 'B5': 83,
  'C6': 84
};

/**
 * Determine which clef to use for a note
 * Treble clef for C4 and above, Bass clef for B3 and below
 */
export function getClefForNote(note) {
  const octave = parseInt(note.slice(-1));
  const noteName = note.slice(0, -1);
  
  if (octave < 4) {
    return 'bass';
  }
  if (octave > 4) {
    return 'treble';
  }
  // Octave 4 - C4 and above are treble
  return 'treble';
}

/**
 * Parse note string into components
 */
export function parseNote(noteStr) {
  const noteName = noteStr.slice(0, -1);
  const octave = parseInt(noteStr.slice(-1));
  return { noteName, octave, full: noteStr };
}

/**
 * Compare two notes (for sorting)
 */
export function compareNotes(a, b) {
  const noteA = parseNote(a);
  const noteB = parseNote(b);
  
  if (noteA.octave !== noteB.octave) {
    return noteA.octave - noteB.octave;
  }
  
  return NOTE_ORDER.indexOf(noteA.noteName) - NOTE_ORDER.indexOf(noteB.noteName);
}

/**
 * Get display name for a note (e.g., "Middle C" for C4)
 */
export function getDisplayName(note) {
  const special = {
    'C4': 'Middle C',
    'A4': 'Concert A'
  };
  
  return special[note] || note;
}

/**
 * Get VexFlow key format
 */
export function getVexFlowKey(note) {
  return VEXFLOW_NOTE_MAP[note] || `${note.toLowerCase().slice(0, -1)}/${note.slice(-1)}`;
}

/**
 * Get the position on piano keyboard (0-based from C3)
 */
export function getPianoKeyPosition(note) {
  const whiteKeys = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const { noteName, octave } = parseNote(note);
  
  // Calculate position from C3
  const octaveOffset = (octave - 3) * 7;
  const noteOffset = whiteKeys.indexOf(noteName);
  
  return octaveOffset + noteOffset;
}

/**
 * Generate piano keys for display
 * Returns array of key objects for a 2-octave range centered on middle C
 */
export function generatePianoKeys(startNote = 'C3', endNote = 'B4') {
  const whiteNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const blackNotePositions = { 'C': true, 'D': true, 'F': true, 'G': true, 'A': true }; // Notes that have a black key to their right
  
  const keys = [];
  const startOctave = parseInt(startNote.slice(-1));
  const endOctave = parseInt(endNote.slice(-1));
  
  for (let octave = startOctave; octave <= endOctave; octave++) {
    for (let i = 0; i < whiteNotes.length; i++) {
      const noteName = whiteNotes[i];
      const fullNote = `${noteName}${octave}`;
      
      // Check if we've passed the end note
      if (octave === endOctave && NOTE_ORDER.indexOf(noteName) > NOTE_ORDER.indexOf(endNote[0])) {
        break;
      }
      
      keys.push({
        note: fullNote,
        isBlack: false,
        isMiddleC: fullNote === 'C4',
        displayLabel: fullNote === 'C4' ? 'C' : noteName
      });
    }
  }
  
  return keys;
}

/**
 * Get mastery level label
 */
export function getMasteryLabel(tier) {
  const labels = ['New', 'Learning', 'Familiar', 'Mastered'];
  return labels[tier] || 'New';
}

/**
 * Get mastery color class
 */
export function getMasteryColorClass(tier) {
  const colors = [
    'bg-cream-300 text-wood-900', // New
    'bg-wood-100 text-wood-700',  // Learning
    'bg-dusty-500/20 text-dusty-500', // Familiar
    'bg-sage-500/20 text-sage-500'  // Mastered
  ];
  return colors[tier] || colors[0];
}

/**
 * Format time in ms to human readable
 */
export function formatResponseTime(ms) {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Format duration in seconds to mm:ss
 */
export function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default {
  NOTE_ORDER,
  VEXFLOW_NOTE_MAP,
  MIDI_NOTE_MAP,
  getClefForNote,
  parseNote,
  compareNotes,
  getDisplayName,
  getVexFlowKey,
  getPianoKeyPosition,
  generatePianoKeys,
  getMasteryLabel,
  getMasteryColorClass,
  formatResponseTime,
  formatDuration
};
